import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  getCoreRowModel,
  getExpandedRowModel,
  useReactTable,
  type ColumnDef,
  type ExpandedState,
} from '@tanstack/react-table';
import { Camera, ExternalLink, Undo2 } from 'lucide-react';
import { fetchNewItemVendors, fetchNewItemsQueue, undoNewItem } from '../api/new_items';
import type { NewItem } from '../api/new_items';
import type { VendorOption } from '../api/checks';
import { useAuth } from '../context/AuthContext';
import ExportButton from '../components/ExportButton';
import NewItemPayloadPanel, { ReturnBadge } from '../components/NewItemPayloadPanel';
import { HeaderActions } from '../components/HeaderActions';
import { LabelBadge } from '../components/badges';
import { Button } from '../components/ui/button';
import { MultiSelect } from '../components/ui/multi-select';
import { DataTable } from '../components/data-table/DataTable';
import { ACTIONS_COL_WIDTH, expandColumn } from '../components/data-table/columns';
import { RowActionButton, RowActionMenu, RowActions, type RowMenuItem } from '../components/data-table/RowActions';
import {
  ColumnsMenu,
  FilterBar,
  FilterRow,
  PageHeading,
  ResetFiltersButton,
  TablePagination,
} from '../components/data-table/TableChrome';
import { fmtDate, fmtDateTime, fmtPrice, fmtVendor } from '../utils/format';
import { restoreRows, takeRows } from '../utils/optimistic';

const PAGE_SIZES = [50, 100, 250, 500, 1000] as const;

const DIMS_SOURCE_STYLES: Record<string, { bg: string; text: string; label: string }> = {
  joes_rules:  { bg: 'bg-blue-100',   text: 'text-blue-800',   label: "Joe's rules" },
  website:     { bg: 'bg-indigo-100',  text: 'text-indigo-800', label: 'Website' },
  ai:          { bg: 'bg-amber-100',   text: 'text-amber-800',  label: 'AI estimate' },
  price_range: { bg: 'bg-red-100',     text: 'text-red-800',    label: 'Price range' },
  manual:      { bg: 'bg-purple-100',  text: 'text-purple-800', label: 'Manual' },
};

function DimsSourceBadge({ source }: { source: string | null }) {
  if (!source) return <span className="text-xs text-zinc-300">—</span>;
  const s = DIMS_SOURCE_STYLES[source] ?? { bg: 'bg-zinc-100', text: 'text-zinc-600', label: source };
  return <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${s.bg} ${s.text}`}>{s.label}</span>;
}

export default function NewItemsQueuePage() {
  const { user } = useAuth();
  const canReview = user?.role === 'reviewer' || user?.role === 'admin';

  const [vendorOptions, setVendorOptions] = useState<VendorOption[]>([]);
  const [vendorCodes, setVendorCodes]     = useState<string[]>([]);
  const [page, setPage]       = useState(1);
  const [perPage, setPerPage] = useState(50);
  const [items, setItems]     = useState<NewItem[]>([]);
  const [total, setTotal]     = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState<string | null>(null);
  const [busy, setBusy]       = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<ExpandedState>({});
  const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>({});

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    setExpanded({});
    try {
      const data = await fetchNewItemsQueue({
        vendor_codes: vendorCodes.length ? vendorCodes : undefined,
        page,
        per_page: perPage,
      });
      setItems(data.items);
      setTotal(data.total);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [vendorCodes, page, perPage]);

  useEffect(() => { void load(); }, [load]);

  useEffect(() => {
    fetchNewItemVendors().then(setVendorOptions).catch(() => setVendorOptions([]));
  }, []);

  async function handleUndo(id: number) {
    const { remaining, snapshot } = takeRows(items, [id]);
    setActionError(null);
    setBusy(true);
    setItems(remaining);
    setTotal(t => t - 1);
    try {
      await undoNewItem(id);
    } catch (e) {
      setItems(prev => restoreRows(prev, snapshot));
      setTotal(t => t + 1);
      const msg = e instanceof Error ? e.message : 'request failed';
      setActionError(`Undo failed — the row was restored. ${msg}`);
    } finally {
      setBusy(false);
    }
  }

  const columns = useMemo<ColumnDef<NewItem>[]>(() => {
    const cols: ColumnDef<NewItem>[] = [];
    cols.push(expandColumn<NewItem>());

    cols.push({
      id: 'sku',
      size: 230,
      enableHiding: false,
      meta: { label: 'SKU' },
      header: 'SKU',
      cell: ({ row }) => (
        <>
          <div className="whitespace-nowrap font-mono text-[12.5px] font-medium">{row.original.sku}</div>
          <div className="max-w-[240px] truncate text-xs text-muted-foreground">
            {row.original.display_name ?? row.original.item_name ?? ''}
          </div>
        </>
      ),
    });

    cols.push({
      id: 'vendor',
      meta: { label: 'Vendor' },
      header: 'Vendor',
      cell: ({ row }) => (
        <span className="whitespace-nowrap text-zinc-700">{fmtVendor(row.original.vendor_name)}</span>
      ),
    });

    cols.push({
      id: 'manufacturer',
      meta: { label: 'Manufacturer' },
      header: 'Manufacturer',
      cell: ({ row }) => <span className="whitespace-nowrap text-zinc-700">{row.original.manufacturer ?? '—'}</span>,
    });

    cols.push({
      id: 'productType',
      meta: { label: 'Product type' },
      header: 'Product type',
      cell: ({ row }) => (
        <span className="whitespace-nowrap text-muted-foreground">
          {row.original.import_payload.product_type ?? '—'}
        </span>
      ),
    });

    cols.push({
      id: 'purchase',
      meta: { label: 'Purchase cost', align: 'right' },
      header: 'Purchase cost',
      cell: ({ row }) => (
        <span className="whitespace-nowrap font-medium">
          {row.original.vendor_price != null ? fmtPrice(row.original.vendor_price) : '—'}
        </span>
      ),
    });

    cols.push({
      id: 'costSource',
      meta: { label: 'Cost source' },
      header: 'Cost source',
      cell: ({ row }) => (
        <span className="whitespace-nowrap text-muted-foreground">
          {row.original.import_payload.cost_source ?? '—'}
        </span>
      ),
    });

    cols.push({
      id: 'dimsSource',
      meta: { label: 'Dims source' },
      header: 'Dims source',
      cell: ({ row }) => <DimsSourceBadge source={row.original.dims_source} />,
    });

    cols.push({
      id: 'dims',
      meta: { label: 'Dims' },
      header: 'Dims',
      cell: ({ row }) => {
        const p = row.original.import_payload;
        if (p.height_inch == null && p.width_inch == null && p.length_inch == null) {
          return <span className="text-xs text-zinc-300">—</span>;
        }
        return (
          <span className="whitespace-nowrap font-mono text-[12px] text-zinc-700">
            {p.height_inch}×{p.width_inch}×{p.length_inch} / {p.item_weight ?? 0} lb
          </span>
        );
      },
    });

    cols.push({
      id: 'priceCategory',
      meta: { label: 'Price cat.' },
      header: 'Price cat.',
      cell: ({ row }) => (
        <span className="whitespace-nowrap text-muted-foreground">
          {row.original.import_payload.item_price_category ?? '—'}
        </span>
      ),
    });

    cols.push({
      id: 'return',
      meta: { label: 'Return' },
      header: 'Return',
      cell: ({ row }) => <ReturnBadge status={row.original.import_payload.return_status} />,
    });

    cols.push({
      id: 'core',
      meta: { label: 'Core', align: 'right' },
      header: 'Core',
      cell: ({ row }) => (
        <span className="whitespace-nowrap text-zinc-700">
          {fmtPrice(row.original.import_payload.core_charge_amount)}
        </span>
      ),
    });

    cols.push({
      id: 'vendorStock',
      meta: { label: 'Vendor stock', align: 'right' },
      header: 'Vendor stock',
      cell: ({ row }) => <span className="text-zinc-700">{row.original.vendor_stock ?? '—'}</span>,
    });

    cols.push({
      id: 'status',
      meta: { label: 'Status' },
      header: 'Status',
      cell: ({ row }) => <LabelBadge value={row.original.status_label} />,
    });

    cols.push({
      id: 'scraped',
      meta: { label: 'Scraped' },
      header: 'Scraped',
      cell: ({ row }) => (
        <span className="whitespace-nowrap text-muted-foreground" title={fmtDateTime(row.original.scraped_at)}>
          {fmtDate(row.original.scraped_at)}
        </span>
      ),
    });

    cols.push({
      id: 'reviewStatus',
      meta: { label: 'Review' },
      header: 'Review',
      cell: ({ row }) => {
        const rs = row.original.review_status;
        if (rs === 'auto_approved') {
          return (
            <span className="rounded-full bg-green-100 px-2 py-0.5 text-[11px] font-medium text-green-800">
              Auto
            </span>
          );
        }
        return (
          <span className="rounded-full bg-blue-100 px-2 py-0.5 text-[11px] font-medium text-blue-800">
            Approved
          </span>
        );
      },
    });

    cols.push({
      id: 'reviewer',
      meta: { label: 'Reviewed by' },
      header: 'Reviewed by',
      cell: ({ row }) => (
        <span className="whitespace-nowrap text-muted-foreground">{row.original.reviewer_email ?? '—'}</span>
      ),
    });

    cols.push({
      id: 'reviewed',
      meta: { label: 'Reviewed' },
      header: 'Reviewed',
      cell: ({ row }) => (
        <span className="whitespace-nowrap text-muted-foreground">
          {fmtDateTime(row.original.reviewed_at ?? row.original.created_at)}
        </span>
      ),
    });

    cols.push({
      id: 'actions',
      size: ACTIONS_COL_WIDTH,
      enableHiding: false,
      meta: { label: 'Actions', align: 'right', padding: 'tight' },
      header: 'Actions',
      cell: ({ row }) => {
        const item = row.original;
        const menuItems: RowMenuItem[] = [];
        if (item.import_payload.url) {
          menuItems.push({ label: 'Open vendor page', icon: ExternalLink, href: item.import_payload.url });
        }
        if (item.screenshot_url) {
          menuItems.push({ label: 'View screenshot', icon: Camera, href: item.screenshot_url });
        }
        return (
          <RowActions>
            {canReview && item.review_status !== 'auto_approved' && (
              <RowActionButton
                label="Return to Review"
                icon={Undo2}
                disabled={busy}
                onClick={() => void handleUndo(item.id)}
              />
            )}
            <RowActionMenu items={menuItems} />
          </RowActions>
        );
      },
    });

    return cols;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canReview, busy]);

  const table = useReactTable({
    data: items,
    columns,
    getRowId: row => String(row.id),
    state: { expanded, columnVisibility },
    onExpandedChange: setExpanded,
    onColumnVisibilityChange: setColumnVisibility,
    getRowCanExpand: () => true,
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    initialState: {
      columnPinning: { left: ['expand', 'sku'], right: ['actions'] },
    },
  });

  const totalPages = Math.max(Math.ceil(total / perPage), 1);

  return (
    <>
      <HeaderActions>
        <ExportButton
          resource="new_items_queue"
          filters={{ vendor_codes: vendorCodes.length ? vendorCodes : undefined }}
          format="csv"
        />
      </HeaderActions>

      <PageHeading title="New Items — Queue" count={total} />

      <p className="mb-4 max-w-3xl text-[13px] text-muted-foreground">
        Approved items waiting to be created in NetSuite. The drain job runs automatically.
      </p>

      <FilterBar>
        <FilterRow>
          <MultiSelect
            label="Vendor"
            options={vendorOptions.map(v => ({ value: v.code, label: fmtVendor(v.name) }))}
            selected={vendorCodes}
            onChange={next => { setVendorCodes(next); setPage(1); }}
          />
          <div className="ml-auto flex items-center gap-2">
            {vendorCodes.length > 0 && (
              <ResetFiltersButton onClick={() => { setVendorCodes([]); setPage(1); }} />
            )}
            <ColumnsMenu table={table} />
          </div>
        </FilterRow>
      </FilterBar>

      {actionError && (
        <div className="mb-3 flex items-center justify-between gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-2.5 text-[13px] text-red-700">
          <span>{actionError}</span>
          <Button variant="ghost" size="sm" onClick={() => setActionError(null)} className="text-red-600">
            Dismiss
          </Button>
        </div>
      )}

      <DataTable
        table={table}
        loading={loading}
        error={error}
        emptyMessage="Queue is empty"
        renderExpanded={row => (
          <NewItemPayloadPanel
            payload={row.original.import_payload}
            dimsSource={row.original.dims_source}
            dimsSourceDetail={row.original.dims_source_detail}
          />
        )}
      />

      <TablePagination
        page={page}
        totalPages={totalPages}
        total={total}
        perPage={perPage}
        pageSizes={PAGE_SIZES}
        onPageChange={setPage}
        onPerPageChange={n => { setPerPage(n); setPage(1); }}
      />
    </>
  );
}
